import React, { useMemo, useState } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { analyzeDataForChart, generateChartConfig, formatTooltipValue } from './chartUtils';
import './ChartVisualization.css';

const ChartVisualization = ({ data, prompt }) => {
  const [selectedChartType, setSelectedChartType] = useState(null);

  // Analyze data and determine optimal chart type
  const analysis = useMemo(() => {
    return analyzeDataForChart(data);
  }, [data]);

  // Generate chart configuration
  const chartConfig = useMemo(() => {
    const chartType = selectedChartType || analysis.chartType;
    const analysisWithOverride = { ...analysis, chartType };
    return generateChartConfig(data, analysisWithOverride);
  }, [data, analysis, selectedChartType]);

  // Available chart types based on data structure
  const availableChartTypes = useMemo(() => {
    if (analysis.chartType === 'none' || analysis.chartType === 'table') {
      return [];
    }

    const types = [];
    
    // Always suggest the auto-detected type
    types.push({
      type: analysis.chartType,
      label: getChartTypeLabel(analysis.chartType),
      icon: getChartTypeIcon(analysis.chartType),
      recommended: true
    });

    // Add alternative chart types based on data structure
    const { labelColumn, valueColumn, xColumn, yColumns } = analysis;

    if (labelColumn && valueColumn && analysis.chartType !== 'bar') {
      types.push({
        type: 'bar',
        label: 'Bar Chart',
        icon: '📊',
        recommended: false
      });
    }

    if (labelColumn && valueColumn && analysis.chartType !== 'pie' && data.length <= 10) {
      types.push({
        type: 'pie',
        label: 'Pie Chart',
        icon: '🥧',
        recommended: false
      });
    }

    if (xColumn && yColumns && analysis.chartType !== 'line') {
      types.push({
        type: 'line',
        label: 'Line Chart',
        icon: '📈',
        recommended: false
      });
    }

    return types;
  }, [analysis, data]);

  // Helper functions
  function getChartTypeLabel(type) {
    const labels = {
      'bar': 'Bar Chart',
      'pie': 'Pie Chart',
      'line': 'Line Chart',
      'groupedBar': 'Grouped Bar Chart',
      'horizontalBar': 'Horizontal Bar Chart',
      'scatter': 'Scatter Plot'
    };
    return labels[type] || 'Chart';
  }

  function getChartTypeIcon(type) {
    const icons = {
      'bar': '📊',
      'pie': '🥧',
      'line': '📈',
      'groupedBar': '📊',
      'horizontalBar': '📊',
      'scatter': '🔀'
    };
    return icons[type] || '📊';
  }

  // Render appropriate chart component
  const renderChart = () => {
    if (!chartConfig) {
      return (
        <div className="chart-error">
          <div className="error-icon">⚠️</div>
          <h4>Cannot create chart</h4>
          <p>{analysis.reason}</p>
        </div>
      );
    }

    const commonProps = {
      data: chartConfig.data,
      options: {
        ...chartConfig.options,
        plugins: {
          ...chartConfig.options.plugins,
          tooltip: {
            callbacks: {
              label: function(context) {
                const datasetLabel = context.dataset.label || '';
                const value = context.parsed.y !== undefined ? context.parsed.y : context.parsed;
                const formattedValue = formatTooltipValue(value, analysis.valueColumn || analysis.valueColumns?.[0]);
                return `${datasetLabel}: ${formattedValue}`;
              }
            }
          }
        }
      }
    };

    switch (chartConfig.type) {
      case 'bar':
        return <Bar {...commonProps} />;
      case 'line':
        return <Line {...commonProps} />;
      case 'pie':
        return <Pie {...commonProps} />;
      default:
        return <Bar {...commonProps} />;
    }
  };

  if (analysis.chartType === 'none') {
    return (
      <div className="chart-visualization">
        <div className="chart-unavailable">
          <div className="unavailable-icon">📊</div>
          <h4>Chart Not Available</h4>
          <p>This data structure is not suitable for visualization.</p>
          <small>Reason: {analysis.reason}</small>
        </div>
      </div>
    );
  }

  if (analysis.chartType === 'table') {
    return (
      <div className="chart-visualization">
        <div className="chart-unavailable">
          <div className="unavailable-icon">📋</div>
          <h4>Chart Not Recommended</h4>
          <p>This data is better displayed as a table.</p>
          <small>Reason: {analysis.reason}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-visualization">
      {/* Chart Type Selector */}
      {availableChartTypes.length > 1 && (
        <div className="chart-controls">
          <div className="chart-type-selector">
            <span className="selector-label">Chart Type:</span>
            <div className="chart-type-buttons">
              {availableChartTypes.map((chartType) => (
                <button
                  key={chartType.type}
                  className={`chart-type-button ${
                    (selectedChartType || analysis.chartType) === chartType.type ? 'active' : ''
                  } ${chartType.recommended ? 'recommended' : ''}`}
                  onClick={() => setSelectedChartType(chartType.type)}
                  title={chartType.recommended ? 'Recommended for this data' : ''}
                >
                  <span className="chart-icon">{chartType.icon}</span>
                  <span className="chart-label">{chartType.label}</span>
                  {chartType.recommended && <span className="recommended-badge">✨</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chart Analysis Info */}
      <div className="chart-info">
        <div className="chart-reasoning">
          <span className="reasoning-icon">💡</span>
          <span className="reasoning-text">{analysis.reason}</span>
        </div>
      </div>

      {/* Chart Container */}
      <div className="chart-container">
        <div className="chart-wrapper">
          {renderChart()}
        </div>
      </div>

      {/* Chart Data Summary */}
      <div className="chart-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Data Points:</span>
            <span className="stat-value">{data.length}</span>
          </div>
          {analysis.valueColumn && (
            <div className="stat-item">
              <span className="stat-label">Metric:</span>
              <span className="stat-value">
                {analysis.valueColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          )}
          {analysis.labelColumn && (
            <div className="stat-item">
              <span className="stat-label">Categories:</span>
              <span className="stat-value">
                {analysis.labelColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartVisualization; 